import React from 'react';
import '../caria.css';
import { TrustChip, DecisionChip, StatusChip } from '../../../commodity/components/ui';
import { VehicleCard } from '../components/VehicleCard';
import { ContactCTA, ContactState } from '../components/ContactCTA';
import { PriceBlock } from '../components/PriceBlock';
import { VehicleDetail } from './VehicleDetail';
import { VehicleFeed } from './VehicleFeed';
import { VehicleDraftCreate } from './VehicleDraftCreate';
import { VehicleEdit } from './VehicleEdit';
import { VehicleMyListings } from './VehicleMyListings';
import {
  VehicleSummary,
  VehicleDetailVM,
  VehicleDraftVM,
  SellerListingVM,
  PublishCompleteness,
} from '../types';

/**
 * CariaUiGallery (Epic 4, S4.4) — the dev-only preview route.
 *
 * The project has no Storybook; the backlog explicitly allows an
 * "equivalent dev-only route". This screen renders every component in
 * every state from docs/research/caria-ui-states.md so QA / UX can
 * eyeball the matrix. Wired at /caria/ui-gallery (App.tsx) and rendered
 * only outside production.
 */
const sampleVehicle: VehicleSummary = {
  id: 'demo-1',
  title: 'Toyota Corolla 2019',
  make: 'Toyota',
  model: 'Corolla',
  year: 2019,
  mileageKm: 64000,
  region: 'CDMX',
  sellerPrice: 245000,
  suggestedPrice: 230000,
  decision: 'overpriced',
  vinState: 'verified',
  status: 'active',
};

const sampleDetail: VehicleDetailVM = {
  id: 'demo-1',
  title: 'Toyota Corolla 2019',
  status: 'active',
  photos: [
    { url: '', alt: 'demo' },
    { url: '', alt: 'demo' },
  ],
  sellerPrice: 245000,
  suggestedPrice: 230000,
  pricingConfidence: 0.78,
  decision: 'overpriced',
  specs: {
    make: 'Toyota',
    model: 'Corolla',
    year: 2019,
    mileageKm: 64000,
    transmission: 'Automática',
    fuel: 'Gasolina',
    color: 'Blanco',
    vin: '1HGCM82633A004352',
    vinState: 'verified',
  },
  trust: { vinState: 'verified', disclosureProvided: true, photoCount: 8, photoComplete: true },
  disclosureText: 'Único dueño. Servicios de agencia al corriente. Sin choques.',
};

const draftSpecs = {
  make: 'Toyota',
  model: 'Corolla',
  year: 2019,
  mileageKm: 64000,
  transmission: 'Automática',
  fuel: 'Gasolina',
  color: 'Blanco',
  vin: '1HGCM82633A004352',
  vinState: 'verified' as const,
};

const completeAll: PublishCompleteness = {
  specs: true,
  vin: true,
  photos: true,
  disclosure: true,
  price: true,
};

const sampleDraftReady: VehicleDraftVM = {
  id: 'draft-ready',
  title: 'Toyota Corolla 2019',
  status: 'draft',
  specs: draftSpecs,
  photos: [
    'EXTERIOR_FRONT',
    'EXTERIOR_REAR',
    'INTERIOR_DASHBOARD',
    'INTERIOR_SEATS',
    'ENGINE',
    'TIRES',
  ],
  disclosureText: 'Único dueño. Servicios de agencia al corriente. Sin choques.',
  listingPrice: 245000,
  suggestedPrice: 230000,
  pricingConfidence: 0.78,
  decision: 'overpriced',
  completeness: completeAll,
};

const sampleDraftGated: VehicleDraftVM = {
  id: 'draft-gated',
  title: 'Honda Civic 2020',
  status: 'draft',
  specs: { ...draftSpecs, make: 'Honda', model: 'Civic', year: 2020, vinState: 'pending' },
  photos: ['EXTERIOR_FRONT', 'EXTERIOR_REAR'],
  completeness: { specs: true, vin: false, photos: false, disclosure: false, price: false },
};

const sampleListings: SellerListingVM[] = [
  {
    id: 'l1',
    title: 'Toyota Corolla 2019',
    status: 'active',
    mileageKm: 64000,
    listingPrice: 245000,
    vinState: 'verified',
  },
  { id: 'l2', title: 'Honda Civic 2020', status: 'draft', mileageKm: 40000, vinState: 'pending' },
  {
    id: 'l3',
    title: 'Nissan Versa 2018',
    status: 'paused',
    mileageKm: 88000,
    listingPrice: 165000,
    vinState: 'verified',
  },
  {
    id: 'l4',
    title: 'Mazda 3 2017',
    status: 'archived',
    mileageKm: 102000,
    listingPrice: 150000,
    vinState: 'failed',
  },
];

const Row: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section>
    <h2>{title}</h2>
    <div className="caria-gallery-page__row">{children}</div>
  </section>
);

export const CariaUiGallery: React.FC = () => {
  if (process.env.NODE_ENV === 'production') {
    return <p style={{ padding: 24 }}>The CAR-IA UI gallery is disabled in production.</p>;
  }

  const contactStates: ContactState[] = ['idle', 'submitting', 'success', 'blocked', 'failed'];

  return (
    <div className="caria-gallery-page" data-testid="caria-ui-gallery">
      <h1>CAR-IA UI Gallery (dev only)</h1>
      <p>Source of truth: docs/research/caria-ui-states.md</p>

      <Row title="TrustChip">
        <TrustChip state="verified" />
        <TrustChip state="pending" />
        <TrustChip state="unknown" />
        <TrustChip state="failed" />
      </Row>

      <Row title="DecisionChip">
        <DecisionChip verdict="good-deal" />
        <DecisionChip verdict="fair-deal" />
        <DecisionChip verdict="overpriced" />
      </Row>

      <Row title="StatusChip">
        <StatusChip status="draft" />
        <StatusChip status="active" />
        <StatusChip status="paused" />
        <StatusChip status="archived" />
      </Row>

      <Row title="VehicleCard — loading / active / pricing-unavailable / seller-paused">
        <div style={{ width: 280 }}>
          <VehicleCard loading />
        </div>
        <div style={{ width: 280 }}>
          <VehicleCard vehicle={sampleVehicle} />
        </div>
        <div style={{ width: 280 }}>
          <VehicleCard
            vehicle={{ ...sampleVehicle, suggestedPrice: undefined, decision: undefined }}
          />
        </div>
        <div style={{ width: 280 }}>
          <VehicleCard vehicle={{ ...sampleVehicle, status: 'paused' }} sellerView />
        </div>
      </Row>

      <Row title="PriceBlock — available / unavailable">
        <div style={{ width: 320 }}>
          <PriceBlock
            sellerPrice={245000}
            suggestedPrice={230000}
            pricingConfidence={0.78}
            decision="overpriced"
          />
        </div>
        <div style={{ width: 320 }}>
          <PriceBlock sellerPrice={245000} />
        </div>
      </Row>

      <Row title="ContactCTA — all states">
        {contactStates.map((s) => (
          <div key={s} style={{ width: 240 }}>
            <ContactCTA state={s} />
          </div>
        ))}
      </Row>

      <section>
        <h2>VehicleFeed — loading / empty / populated</h2>
        <VehicleFeed loading />
        <VehicleFeed vehicles={[]} />
        <VehicleFeed
          vehicles={[sampleVehicle, { ...sampleVehicle, id: 'demo-2', decision: 'good-deal' }]}
          pageCount={3}
        />
      </section>

      <section>
        <h2>VehicleDetail — loading / active</h2>
        <VehicleDetail loading />
        <VehicleDetail vehicle={sampleDetail} />
      </section>

      <section>
        <h2>VehicleDraftCreate (S6.8) — initial form</h2>
        <VehicleDraftCreate />
      </section>

      <section>
        <h2>VehicleEdit (S6.8) — gated (VIN/photos/disclosure/price pending) vs. ready</h2>
        <VehicleEdit vehicle={sampleDraftGated} />
        <VehicleEdit vehicle={sampleDraftReady} />
      </section>

      <section>
        <h2>VehicleMyListings (S6.8) — loading / empty / populated</h2>
        <VehicleMyListings loading />
        <VehicleMyListings listings={[]} />
        <VehicleMyListings listings={sampleListings} onCreate={() => undefined} />
      </section>
    </div>
  );
};

export default CariaUiGallery;
